import { motion, useSpring } from "motion/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/utils";
import { useChartConfig } from "../chart-config-context";
import { chartCssVars } from "../chart-context";

// Inner-only-on-visible so `useSpring` initializes at the cursor's actual x/y
// instead of (0, 0) on first hover.
export function TooltipBox(props) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const container = props.containerRef.current;
  if (!(mounted && container)) {
    return null;
  }
  if (!props.visible) {
    return null;
  }
  return <TooltipBoxInner {...props} container={container} />;
}

function TooltipBoxInner({
  x,
  y,
  containerWidth,
  containerHeight,
  offset = 16,
  className = "",
  children,
  left: leftOverride,
  top: topOverride,
  flipped: flippedOverride,
  springConfig,
  animate = true,
  entrance = true,
  panelStyle,
  backgroundColor = chartCssVars.tooltipBackground,
  container
}) {
  const { tooltipBoxSpring } = useChartConfig();
  const effectiveSpring = springConfig ?? tooltipBoxSpring;

  const tooltipRef = useRef(null);
  const tooltipWidthRef = useRef(180);
  const tooltipHeightRef = useRef(80);
  const [staticPosition, setStaticPosition] = useState({ left: x, top: y });

  // Keep the panel inside the chart container on both axes — an unclamped
  // flip can push it past the left edge and over a neighboring card.
  const clampX = (value, width) =>
    Math.max(0, Math.min(value, Math.max(0, containerWidth - width)));

  const tw = tooltipWidthRef.current;
  const th = tooltipHeightRef.current;
  const shouldFlipX = x + tw + offset > containerWidth;
  const targetX = clampX(shouldFlipX ? x - offset - tw : x + offset, tw);
  const targetY = Math.max(0, Math.min(y - th / 2, Math.max(0, containerHeight - th)));

  const animatedLeft = useSpring(targetX, effectiveSpring);
  const animatedTop = useSpring(targetY, effectiveSpring);

  if (animate && leftOverride === undefined) {
    animatedLeft.set(targetX);
  }
  if (animate && topOverride === undefined) {
    animatedTop.set(targetY);
  }

  useLayoutEffect(() => {
    if (!tooltipRef.current) {
      return;
    }
    const el = tooltipRef.current;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (w > 0) {
      tooltipWidthRef.current = w;
    }
    if (h > 0) {
      tooltipHeightRef.current = h;
    }
    const w2 = tooltipWidthRef.current;
    const h2 = tooltipHeightRef.current;
    const flip = x + w2 + offset > containerWidth;
    const tx = Math.max(
      0,
      Math.min(flip ? x - offset - w2 : x + offset, Math.max(0, containerWidth - w2))
    );
    const ty = Math.max(0, Math.min(y - h2 / 2, Math.max(0, containerHeight - h2)));
    if (!animate) {
      setStaticPosition({ left: tx, top: ty });
      return;
    }
    if (leftOverride === undefined) {
      animatedLeft.set(tx);
    }
    if (topOverride === undefined) {
      animatedTop.set(ty);
    }
  }, [
    x,
    y,
    containerWidth,
    containerHeight,
    offset,
    leftOverride,
    topOverride,
    animate,
    animatedLeft,
    animatedTop,
  ]);

  const prevFlipRef = useRef(shouldFlipX);
  const [flipKey, setFlipKey] = useState(0);

  useEffect(() => {
    if (prevFlipRef.current !== shouldFlipX) {
      setFlipKey((k) => k + 1);
      prevFlipRef.current = shouldFlipX;
    }
  }, [shouldFlipX]);

  const finalLeft = animate
    ? (leftOverride ?? animatedLeft)
    : staticPosition.left;
  const finalTop = animate ? (topOverride ?? animatedTop) : staticPosition.top;
  const isFlipped = flippedOverride ?? shouldFlipX;
  const transformOrigin = isFlipped ? "right top" : "left top";

  const panelClassName = cn(
    "max-w-[220px] overflow-hidden rounded-md text-chart-tooltip-foreground shadow-md ring-1 ring-black/5",
    panelStyle?.backgroundColor === undefined &&
      backgroundColor === chartCssVars.tooltipBackground &&
      "bg-chart-tooltip-background",
    panelStyle?.backdropFilter === undefined && "backdrop-blur-md"
  );
  const panelStyleResolved = {
    transformOrigin,
    ...(panelStyle?.backgroundColor === undefined && {
      backgroundColor,
    }),
    ...panelStyle,
  };

  if (!entrance) {
    return createPortal(<div
      className={cn("pointer-events-none absolute z-50", className)}
      ref={tooltipRef}
      style={{ left: staticPosition.left, top: staticPosition.top }}>
      <div className={panelClassName} style={panelStyleResolved}>
        {children}
      </div>
    </div>, container);
  }

  return createPortal(<motion.div
    animate={{ opacity: 1 }}
    className={cn("pointer-events-none absolute z-50", className)}
    exit={{ opacity: 0 }}
    initial={{ opacity: 0 }}
    ref={tooltipRef}
    style={{ left: finalLeft, top: finalTop }}
    transition={{ duration: 0.1 }}>
    <motion.div
      animate={{ scale: 1, opacity: 1, x: 0 }}
      className={panelClassName}
      initial={{ scale: 0.96, opacity: 0, x: isFlipped ? 8 : -8 }}
      key={flipKey}
      style={panelStyleResolved}
      transition={{ duration: 0.15, ease: "easeOut" }}>
      {children}
    </motion.div>
  </motion.div>, container);
}

TooltipBox.displayName = "TooltipBox";

export default TooltipBox;
